import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTools } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { getMaintenanceSettings } from '../Redux/Slice/maintenance.slice';
import BackgroundColor from '../component/BackgroundColor';
import AnimatedSparkle from '../component/AnimatedSparkle';
import { IoGameControllerOutline } from "react-icons/io5";

const Maintenance = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { maintenance } = useSelector((state) => state.maintenance);
    const [timeLeft, setTimeLeft] = useState("");

    useEffect(() => {
        if (!maintenance) {
            dispatch(getMaintenanceSettings());
        }
    }, [dispatch, maintenance]);

    useEffect(() => {
        if (!maintenance?.completionTime) return;

        const calculateTimeLeft = () => {
            const difference = new Date(maintenance.completionTime) - new Date();
            if (difference <= 0) {
                return "00:00:00";
            }

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((difference / 1000 / 60) % 60);
            const seconds = Math.floor((difference / 1000) % 60);

            const formatNumber = (num) => String(num).padStart(2, '0');

            if (days > 0) {
                return `${days}d ${formatNumber(hours)}h ${formatNumber(minutes)}m ${formatNumber(seconds)}s`;
            } else {
                return `${formatNumber(hours)}h ${formatNumber(minutes)}m ${formatNumber(seconds)}s`;
            }
        };

        setTimeLeft(calculateTimeLeft());

        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [maintenance?.completionTime]);

    const headingText = maintenance?.heading || "SYSTEM ALERT";
    const subtitleText = maintenance?.subtitle || "WE'LL BE BACK SHORTLY";
    const messageText = maintenance?.message || "We're currently experiencing a technical issue. Our team is working to restore services as quickly as possible.";


    return (
        <BackgroundColor className="flex items-center justify-center overflow-hidden relative">
            {/* Grid Pattern Overlay */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255, 255, 255, 0.4) 2px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.4) 2px, transparent 1px)",
                        backgroundSize: "100px 100px",
                    }}
                />
            </div>

            {/* Decorative Sparkles */}
            <AnimatedSparkle
                size={60}
                className="absolute top-20 left-[15%] text-secondary opacity-60"
                animation="rotate-cw"
            />
            <AnimatedSparkle
                size={40}
                className="absolute bottom-20 right-[15%] text-primary opacity-60"
                animation="rotate-ccw"
            />
            <AnimatedSparkle
                size={80}
                className="absolute top-1/3 right-[10%] text-primary opacity-30"
                animation="scale"
            />

            {/* Background Controller Icon */}
            <IoGameControllerOutline
                size={300}
                className="text-primary/5 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 animate-pulse"
            />

            <div className="relative z-10 max-w-2xl px-6 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="flex flex-col items-center"
                >
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                        <motion.div
                            animate={{
                                rotate: [0, 10, -10, 0],
                                y: [0, -15, 0]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <FaTools size={100} className="text-secondary relative z-10 drop-shadow-[0_0_15px_rgba(167,0,111,0.5)]" />
                        </motion.div>
                    </div>

                    <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                        {headingText}
                    </h2>

                    <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-wider uppercase">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                            {subtitleText}
                        </span>
                    </h1>

                    <p className="text-lg md:text-xl text-gray-400 mb-6 max-w-lg mx-auto leading-relaxed">
                        {messageText}
                    </p>

                    {timeLeft && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                            className="mt-4 px-6 py-3.5 bg-white/5 border border-white/10 rounded-2xl inline-block backdrop-blur-md"
                        >
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Expected Completion In</p>
                            <p className="text-lg font-bold text-pink-500 mt-1">{timeLeft}</p>
                        </motion.div>
                    )}

                </motion.div>
            </div>

            {/* Footer Text */}
            <div className="absolute bottom-8 left-0 right-0 text-center text-gray-500 text-sm">
                &copy; {new Date().getFullYear()} YOYO Games. All rights reserved.
            </div>
        </BackgroundColor>
    );
};

export default Maintenance;
